import type { ReactNode } from "react"

import { Card } from "@/ui/card"
import { cn } from "@/ui/utils"

type TechLogo = {
  name: string
  href: string
  logo: ReactNode
}

function SimpleIcon({ slug, color }: { slug: string; color: string }) {
  return (
    <img
      src={`https://cdn.simpleicons.org/${slug}/${color}`}
      alt=""
      width={32}
      height={32}
      loading="lazy"
      decoding="async"
      className="size-8"
      aria-hidden
    />
  )
}

/** Playwright is missing from cdn.simpleicons.org; same path as Simple Icons with brand green. */
function PlaywrightIcon() {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="size-8" aria-hidden>
      <title>Playwright</title>
      <path
        fill="#2EAD33"
        d="M23.996 7.462c-.056.837-.257 2.135-.716 3.85-.995 3.715-4.27 10.874-10.42 9.227-6.15-1.65-5.407-9.487-4.412-13.201.46-1.716.934-2.94 1.305-3.694.42-.853.846-.289 1.815.523.684.573 2.41 1.791 5.011 2.488 2.601.697 4.706.506 5.583.352 1.245-.219 1.897-.494 1.834.455Zm-9.807 3.863s-.127-1.819-1.773-2.286c-1.644-.467-2.613 1.04-2.613 1.04Zm4.058 4.539-7.769-2.172s.446 2.306 3.338 3.153c2.862.836 4.43-.98 4.43-.981Zm2.701-2.51s-.13-1.818-1.773-2.286c-1.644-.469-2.612 1.038-2.612 1.038ZM8.57 18.23c-4.749 1.279-7.261-4.224-8.021-7.08C.197 9.831.044 8.832.003 8.188c-.047-.73.455-.52 1.415-.354.677.118 2.3.261 4.308-.28a11.28 11.28 0 0 0 2.41-.956c-.058.197-.114.4-.17.61-.433 1.618-.827 4.055-.632 6.426-1.976.732-2.267 2.423-2.267 2.423l2.524-.715c.227 1.002.6 1.987 1.15 2.838a5.914 5.914 0 0 1-.171.049Zm-4.188-6.298c1.265-.333 1.363-1.631 1.363-1.631l-3.374.888s.745 1.076 2.01.743Z"
      />
    </svg>
  )
}

const CORE_TECH_STACK: TechLogo[] = [
  {
    name: "Cloudflare",
    href: "https://workers.cloudflare.com/",
    logo: <SimpleIcon slug="cloudflare" color="F38020" />,
  },
  {
    name: "TanStack Start",
    href: "https://tanstack.com/start",
    logo: <SimpleIcon slug="tanstack" color="EAB308" />,
  },
  {
    name: "Vite",
    href: "https://vite.dev/",
    logo: <SimpleIcon slug="vite" color="646CFF" />,
  },
  {
    name: "React",
    href: "https://react.dev/",
    logo: <SimpleIcon slug="react" color="61DAFB" />,
  },
  {
    name: "Tailwind CSS",
    href: "https://tailwindcss.com/",
    logo: <SimpleIcon slug="tailwindcss" color="06B6D4" />,
  },
  {
    name: "Drizzle ORM",
    href: "https://orm.drizzle.team/",
    logo: <SimpleIcon slug="drizzle" color="C5F74F" />,
  },
  {
    name: "TypeScript",
    href: "https://www.typescriptlang.org/",
    logo: <SimpleIcon slug="typescript" color="3178C6" />,
  },
]

const TECH_STACK: TechLogo[] = [
  {
    name: "Vite",
    href: "https://vite.dev/",
    logo: <SimpleIcon slug="vite" color="646CFF" />,
  },
  {
    name: "React",
    href: "https://react.dev/",
    logo: <SimpleIcon slug="react" color="61DAFB" />,
  },
  {
    name: "TanStack Start",
    href: "https://tanstack.com/start",
    logo: <SimpleIcon slug="tanstack" color="EAB308" />,
  },
  {
    name: "Cloudflare Workers",
    href: "https://workers.cloudflare.com/",
    logo: <SimpleIcon slug="cloudflare" color="F38020" />,
  },
  {
    name: "Cloudflare D1",
    href: "https://www.cloudflare.com/developer-platform/products/d1/",
    logo: <SimpleIcon slug="cloudflare" color="F38020" />,
  },
  {
    name: "TypeScript",
    href: "https://www.typescriptlang.org/",
    logo: <SimpleIcon slug="typescript" color="3178C6" />,
  },
  {
    name: "Tailwind CSS",
    href: "https://tailwindcss.com/",
    logo: <SimpleIcon slug="tailwindcss" color="06B6D4" />,
  },
  {
    name: "Drizzle ORM",
    href: "https://orm.drizzle.team/",
    logo: <SimpleIcon slug="drizzle" color="C5F74F" />,
  },
  {
    name: "Oxc",
    href: "https://oxc.rs/",
    logo: <SimpleIcon slug="oxc" color="00F7F1" />,
  },
  {
    name: "Vitest",
    href: "https://vitest.dev/",
    logo: <SimpleIcon slug="vitest" color="6E9F18" />,
  },
  {
    name: "Playwright",
    href: "https://playwright.dev/",
    logo: <PlaywrightIcon />,
  },
  {
    name: "Bun",
    href: "https://bun.sh/",
    logo: <SimpleIcon slug="bun" color="FBF0DF" />,
  },
  {
    name: "shadcn/ui",
    href: "https://ui.shadcn.com/",
    logo: <SimpleIcon slug="shadcnui" color="FFFFFF" />,
  },
  {
    name: "Storybook",
    href: "https://storybook.js.org/",
    logo: <SimpleIcon slug="storybook" color="FF4785" />,
  },
  {
    name: "Better Auth",
    href: "https://www.better-auth.com/",
    logo: <SimpleIcon slug="betterauth" color="FFFFFF" />,
  },
  {
    name: "Polar.sh",
    href: "https://polar.sh/",
    logo: (
      <img
        src="https://polar.sh/favicon.ico"
        alt=""
        width={32}
        height={32}
        loading="lazy"
        decoding="async"
        className="size-8 object-contain"
        aria-hidden
      />
    ),
  },
  {
    name: "Resend",
    href: "https://resend.com/",
    logo: <SimpleIcon slug="resend" color="FFFFFF" />,
  },
  {
    name: "Cloudflare Turnstile",
    href: "https://www.cloudflare.com/products/turnstile/",
    logo: <SimpleIcon slug="cloudflare" color="F38020" />,
  },
]

export function CoreTechStackLogos({ className }: { className?: string }) {
  return (
    <ul
      className={cn(
        "mx-auto flex max-w-48 flex-wrap items-center justify-center gap-5 md:max-w-none",
        className,
      )}
    >
      {CORE_TECH_STACK.map((item) => (
        <li key={item.name}>
          <a
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={item.name}
            title={item.name}
            className="opacity-60 transition-opacity hover:opacity-100"
          >
            {item.logo}
          </a>
        </li>
      ))}
    </ul>
  )
}

export function TechStackLogos({ className }: { className?: string }) {
  return (
    <ul
      className={cn(
        "mx-auto grid max-w-5xl grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6",
        className,
      )}
    >
      {TECH_STACK.map((item) => (
        <li key={item.name}>
          <a href={item.href} target="_blank" rel="noopener noreferrer" className="block">
            <Card className="border-border/60 hover:border-border hover:bg-card/50 items-center gap-2 rounded-lg px-3 py-4 text-center shadow-none transition-colors">
              {item.logo}
              <span className="text-muted-foreground text-xs leading-tight">{item.name}</span>
            </Card>
          </a>
        </li>
      ))}
    </ul>
  )
}
